import clsx from "clsx";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ChevronLeft } from "@/assets/icons/ds";
import { ChevronDown } from "@/assets/icons/icons";
import { Button } from "@/components/Interface/Button/Button";
import { CardContainer } from "@/components/Layout/BaseLayouts/CardContainer";

export interface RoamingBookingPageProps {
  dataTestid?: string;
  title: string;
  /** 1-based position in the booking flow. */
  step: number;
  /** Total number of steps in the flow. Defaults to 2 (package selection + checkout). */
  totalSteps?: number;
  /** Label under the progress bar, e.g. the step's name. */
  stepLabel: string;
  onBack: () => void;
  children: ReactNode;
}

/**
 * Page shell of the roaming booking flow: renders a back button, the page
 * headline and a step progress bar; the routed step's content renders below.
 */
export const RoamingBookingPage = ({
  dataTestid,
  title,
  step,
  totalSteps = 2,
  stepLabel,
  onBack,
  children,
}: RoamingBookingPageProps) => {
  const { t } = useTranslation();

  return (
    <section className="w-full" data-testid={dataTestid}>
      <CardContainer
        className="flex w-full flex-col gap-6 rounded-lg p-6"
        variant="ivory"
      >
        <Button
          dataTestid="roaming-booking-back"
          variant="tertiary"
          ariaLabel={t("common.buttons.back")}
          icon={<ChevronLeft />}
          onClick={onBack}
        />

        <div className="flex w-full flex-col gap-4">
          <h1 className="text-headline-xl font-semibold leading-xs tracking-[-0.02em] text-foreground-primary">
            {title}
          </h1>
          <div className="flex w-full flex-col gap-2">
            {/* DireProgressBar is a continuous 0-100 fill and collapses its
                second segment at value=100 - wrong for a discrete N-step
                indicator, where every step's block must stay visible even
                once done. Rendered as N blocks instead, reusing its exact
                fill/track tokens (bg-surface-primary / bg-surface-secondary)
                so this matches the sibling component's palette. */}
            <div className="flex w-full gap-0.5" aria-hidden="true">
              {Array.from({ length: totalSteps }, (_, index) => (
                <span
                  key={index}
                  className={clsx(
                    "h-1 flex-1 rounded-full",
                    index + 1 <= step
                      ? "bg-surface-primary"
                      : "bg-surface-secondary",
                  )}
                />
              ))}
            </div>
            <div className="flex w-full items-center justify-between gap-4">
              <p className="text-body-sm font-semibold leading-none text-foreground-primary">
                {stepLabel}
              </p>
              <p className="flex items-center gap-1 text-body-sm leading-none text-foreground-primary">
                {`${String(step).padStart(2, "0")} / ${String(totalSteps).padStart(2, "0")}`}
                <ChevronDown className="size-4" />
              </p>
            </div>
          </div>
        </div>

        {children}
      </CardContainer>
    </section>
  );
};
